import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronUp, ChevronDown, X } from 'lucide-react';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Badge } from '../components/ui/badge';
import { Alert } from '../components/ui/alert';
import CustomerApi from '../customer/customerApi';
import { Customer } from '../customer/customerType';

const TestingPage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Customer', path: '/main/customer' },
  ];
  
  const navigate = useNavigate();
  
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  
  const selectedCustomer = customers.find(c => c.customer_id === selectedCustomerId) || null;
  
  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        const fetchedCustomers = await CustomerApi.get();
        //console.log('Fetched customers:', fetchedCustomers);
        setCustomers(fetchedCustomers);
      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('An unknown error occurred.');
        }
      }
    };

    fetchCustomer();
  }, []);
  
  const handleSortByLastName = (order: 'asc' | 'desc') => {
    const sortedCustomers = [...customers].sort((a, b) => {
      if (order === 'asc') {
        return a.last_name.localeCompare(b.last_name);
      } else {
        return b.last_name.localeCompare(a.last_name);
      }
    });
    setCustomers(sortedCustomers);
  };
  
  const handleClearSelection = () => {
    setSelectedCustomerId(null);
  };
  
  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />
        
        <div className="mt-6">
          <h2 className="text-2xl font-semibold mb-6">Testing with REAL API Data</h2>
          
          {errorMessage && (
            <Alert variant="error" className="mb-6">
              {errorMessage}
            </Alert>
          )}
          
          <div className="mb-4 p-4 bg-blue-50 rounded">
            <p><strong>Selected ID:</strong> {selectedCustomerId || 'None'}</p>
            <p><strong>Selected ID Type:</strong> {typeof selectedCustomerId}</p>
            {selectedCustomer && (
              <p><strong>Selected Customer:</strong> {selectedCustomer.first_name} {selectedCustomer.last_name}</p>
            )}
          </div>
          
          <div className="mb-6 p-4 bg-gray-50 rounded-lg flex flex-wrap items-center gap-3 text-sm">
            <div className="flex items-center gap-2">
              {selectedCustomer ? (
                <>
                  <button onClick={handleClearSelection} className="text-gray-600 hover:text-gray-900">
                    <X className="size-5" />
                  </button>
                  <Badge variant="default">1 customer selected</Badge>
                </>
              ) : (
                <span className="text-gray-500 text-sm">No customer selected</span>
              )}
            </div>
          </div>
          
          {customers.length > 0 ? (
            <div className="hidden md:block overflow-x-auto border border-gray-200 rounded-lg">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="w-12 px-4 py-3"></th>
                    <th className="px-4 py-3 text-left">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">Name</span>
                        <div className="flex flex-col">
                          <button onClick={() => handleSortByLastName('asc')}>
                            <ChevronUp className="size-3" />
                          </button>
                          <button onClick={() => handleSortByLastName('desc')}>
                            <ChevronDown className="size-3" />
                          </button>
                        </div>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left font-semibold">Email</th>
                    <th className="px-4 py-3 text-left font-semibold">Phone</th>
                    <th className="px-4 py-3 text-left font-semibold">ID Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {customers.map((customer) => (
                    <tr key={customer.customer_id}>
                      <td className="px-4 py-3 text-center">
                        <input
                          type="radio"
                          name="customerSelect"
                          onChange={() => {
                            //console.log('=== Radio Clicked ===');
                            //console.log('Customer ID:', customer.customer_id);
                            //console.log('Customer ID Type:', typeof customer.customer_id);
                            //console.log('Current selectedCustomerId:', selectedCustomerId);
                            //console.log('Are they equal?', selectedCustomerId === customer.customer_id);
                            setSelectedCustomerId(customer.customer_id);
                          }}
                          checked={selectedCustomerId === customer.customer_id}
                          className="size-4 cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3">
                        {customer.first_name} {customer.last_name}
                      </td>
                      <td className="px-4 py-3">{customer.email}</td>
                      <td className="px-4 py-3">{customer.phone}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {typeof customer.customer_id}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>Loading customers...</p>
          )}
        </div>
      </div>
    </>
  );
};

export default TestingPage;